import {
    EnumButtonName,
    EnumDialogName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import commonMessages from '@rhg/lang/messages/common';
import { ButtonProps, SxProps, Theme } from '@rhg/libs/material';
import { SimpleDialogProps } from '@rhg/libs/material/simple-dialog';
import {
    BodyTextAlign,
    IconColor,
} from '@rhg/libs/material/simple-dialog/types';
import { ChoiceDialogButton } from '@rhg/providers/choice-dialog';
import { ReactElement, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import useChoiceDialog from '../use-choice-dialog';

export interface ConfirmationOptions {
    trackDialog?: EnumDialogName;
    /** Screen and feature for the dialog view and its buttons, for a dialog opened from a surface that is not the page itself. */
    trackScreen?: EnumScreenName;
    trackFeature?: EnumFeature;
    trackProps?: EventProperties;
    body?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    title?: string;
    icon?: ReactElement;
    fullScreen?: boolean;
    background?: SimpleDialogProps['background'];
    iconColor?: IconColor;
    confirmLabel?: string;
    confirmTrackButton?: EnumButtonName;
    confirmTrackButtonProps?: EventProperties;
    noCancelButton?: boolean;
    cancelLabel?: string;
    /** Defaults to `outlined`. */
    cancelVariant?: ButtonProps['variant'];
    confirmIcon?: ReactNode;
    confirmButtonStyle?: SxProps<Theme>;
    cancelTrackButton?: EnumButtonName;
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<SimpleDialogProps>;
    hideConfirmButton?: boolean;
}

/**
 * Before using this component directly, see if you can use the `RegistrationSimpleDialog` component instead as it provides styling that aligns more directly with our design system.
 */
export default function useConfirmation() {
    const { choose, isOpen } = useChoiceDialog();
    const { formatMessage } = useIntl();

    const confirm = useCallback(
        async (options: ConfirmationOptions) => {
            const { trackScreen, trackFeature } = options;
            const choice = await choose({
                ...options,
                SimpleDialogProps: {
                    trackScreen,
                    trackFeature,
                    ...options.SimpleDialogProps,
                },
                buttons: [
                    ...(options?.noCancelButton
                        ? []
                        : [
                              {
                                  title:
                                      options?.cancelLabel ||
                                      formatMessage(
                                          commonMessages['common.cancel']
                                      ),
                                  variant: options?.cancelVariant ?? 'outlined',
                                  trackButton:
                                      options?.cancelTrackButton ||
                                      EnumButtonName.Cancel,
                                  trackScreen,
                                  trackFeature,
                              },
                          ]),
                    ...(!options.hideConfirmButton
                        ? [
                              {
                                  title:
                                      options?.confirmLabel ||
                                      formatMessage(
                                          commonMessages['common.confirm']
                                      ),
                                  variant: 'contained',
                                  startIcon: options?.confirmIcon,
                                  trackButton:
                                      options?.confirmTrackButton ||
                                      EnumButtonName.Confirm,
                                  trackProps: options?.confirmTrackButtonProps,
                                  trackScreen,
                                  trackFeature,
                                  sx: options?.confirmButtonStyle,
                              },
                          ]
                        : []),
                ] as ChoiceDialogButton[],
            });
            // The confirmation button will be the choice with index 1 if there is no cancel button, otherwise, it will be choice 2
            return (options?.noCancelButton && choice === 1) || choice === 2;
        },
        [choose, formatMessage]
    );

    return { confirm, isOpen };
}
